/**
 * Security utility functions for validation and sanitization
 */

/**
 * Validate if URL is safe for href attribute
 * Prevents javascript:, data:, and dangerous protocols
 * P0 - XSS Prevention
 */
export const isSafeHref = (url) => {
  if (!url || typeof url !== "string") return false;

  const trimmed = url.trim().toLowerCase();

  // Blocked protocols
  const dangerousProtocols = [
    "javascript:",
    "data:",
    "vbscript:",
    "file:",
    "about:",
  ];

  for (const protocol of dangerousProtocols) {
    if (trimmed.startsWith(protocol)) return false;
  }

  // Allow relative URLs and http/https only
  if (trimmed.startsWith("http://") || trimmed.startsWith("https://")) {
    return true;
  }

  // Allow relative URLs starting with / or ?
  if (trimmed.startsWith("/") || trimmed.startsWith("?")) {
    return true;
  }

  // Allow hash links
  if (trimmed.startsWith("#")) {
    return true;
  }

  return false;
};

/**
 * Validate and sanitize CDN URL base
 * Prevents domain hijacking and redirection attacks
 * P1 - DDoS/SSRF Prevention
 */
export const isValidCdnUrl = (cdnUrl, allowedDomains = []) => {
  if (!cdnUrl || typeof cdnUrl !== "string") return false;

  try {
    const url = new URL(cdnUrl);

    // Only allow https
    if (url.protocol !== "https:") return false;

    // Check against whitelist if provided
    if (allowedDomains && allowedDomains.length > 0) {
      const hostname = url.hostname.toLowerCase();
      return allowedDomains.some((domain) =>
        hostname.endsWith(domain.toLowerCase()),
      );
    }

    return true;
  } catch (error) {
    return false;
  }
};

/**
 * Safely escape string for HTML attributes
 * P1 - XSS Prevention
 */
export const escapeHtmlAttribute = (str) => {
  if (!str || typeof str !== "string") return "";

  const map = {
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#39;",
  };

  return str.replace(/[&<>"']/g, (char) => map[char]);
};

/**
 * Validate language parameter
 * Prevents injection attacks via cookie
 * P1 - Injection Prevention
 */
export const isValidLanguage = (lang, allowedLanguages = ["mn", "en"]) => {
  if (!lang || typeof lang !== "string") return false;

  const trimmed = lang.trim().toLowerCase();

  // Only alphanumeric, 2-5 chars
  if (!/^[a-z]{2,5}$/.test(trimmed)) return false;

  return allowedLanguages.some((allowed) => allowed.toLowerCase() === trimmed);
};

/**
 * Sanitize filename to prevent path traversal
 * P1 - Path Traversal Prevention
 */
export const sanitizeFilename = (filename) => {
  if (!filename || typeof filename !== "string") return "";

  // Remove path traversal attempts
  return filename.replace(/\.\.\//g, "").replace(/\.\.\\/g, "");
};

/**
 * Rate limit check (simple in-memory, use Redis in production)
 * P1 - DDoS Prevention
 */
const requestCounts = new Map();

export const checkRateLimit = (key, maxRequests = 100, windowMs = 60000) => {
  const now = Date.now();
  const entry = requestCounts.get(key) || [];

  // Remove old entries outside window
  const validEntries = entry.filter((timestamp) => now - timestamp < windowMs);

  if (validEntries.length >= maxRequests) {
    return false; // Rate limit exceeded
  }

  validEntries.push(now);
  requestCounts.set(key, validEntries);

  // Cleanup old entries
  if (Math.random() < 0.01) {
    for (const [k, v] of requestCounts.entries()) {
      const filtered = v.filter((timestamp) => now - timestamp < windowMs);
      if (filtered.length === 0) {
        requestCounts.delete(k);
      } else {
        requestCounts.set(k, filtered);
      }
    }
  }

  return true;
};
